// The decision box of a task that waits for a person (« À RÉGLER », « BLOQUÉ »), in the task drawer:
// the points the agent proposes as checkboxes (all ticked), an instruction (« fais ça, pas ça »),
// and FAIRE CE QUI EST COCHÉ / TOUT FAIRE. The same agent gets a follow-up task with exactly what
// was accepted (POST /api/tasks/:id/decide); this task is then marked settled. Once decided, the
// drawer shows the decision instead: what was accepted or refused, the instruction, the follow-up.
import { h, button, textarea, stateBadge, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { formatDateTime } from '../../format.js';
import { section } from './components.js';
import { applyTaskResponse } from './approvalbox.js';
import './strings.js';

export const DECISION_COMMENT_MAX = 4000;

/** Does this task wait for a decision this person may take (pure)? */
export function canDecide(task, { canOperate = false } = {}) {
  return Boolean(canOperate && task && task.state === 'done' && (task.todo || task.blocked)
    && !task.settled && !task.ignored && !task.decision);
}

/**
 * The body of POST /api/tasks/:id/decide, or null when nothing is chosen (pure).
 * @param {{ticked: string[], comment: string, all?: boolean}} choice
 */
export function decisionBody({ ticked = [], comment = '', all = false } = {}) {
  const note = String(comment ?? '').trim().slice(0, DECISION_COMMENT_MAX);
  if (all) return { all: true, ...(note ? { comment: note } : {}) };
  if (!ticked.length && !note) return null;
  return { accepted: [...ticked], ...(note ? { comment: note } : {}) };
}

/**
 * The decision box (a drawer section) of a task that waits for one.
 * @param {object} ctx app context (api, toast)
 * @param {object} task the task detail (id, proposals, agentName)
 * @param {{onDone?: (res: object) => void}} [opts]
 * @returns {HTMLElement}
 */
export function createDecisionBox(ctx, task, { onDone = () => {} } = {}) {
  const path = `/api/tasks/${encodeURIComponent(String(task.id))}/decide`;
  const agent = task.agentName || t('td.unknownAgent');
  const points = Array.isArray(task.proposals) ? task.proposals.filter((p) => p && typeof p.id === 'string') : [];
  const boxes = points.map((p) => h('input', { type: 'checkbox', class: 'td-decide-check', checked: true, value: p.id, 'aria-label': p.text }));
  const area = textarea({
    class: 'td-decide-comment', rows: 3, maxLength: DECISION_COMMENT_MAX,
    placeholder: t(points.length ? 'td.decide.placeholder' : 'td.decide.placeholderFree'),
    'aria-label': t('td.decide.comment', { agent }),
  });
  const error = h('p', { class: 'td-decide-error', role: 'alert', hidden: true });
  let busy = false;
  const send = async (all) => {
    if (busy) return;
    const body = decisionBody({ ticked: boxes.filter((b) => b.checked).map((b) => b.value), comment: area.value, all });
    if (!body) {
      error.hidden = false;
      setText(error, t('td.decide.empty'));
      return;
    }
    busy = true;
    for (const b of [doSelected, doAll]) b.disabled = true;
    try {
      const res = await ctx.api.post(path, body);
      applyTaskResponse(ctx, res?.task);
      ctx.toast?.success?.(t('td.decide.done', { agent }));
      onDone(res);
    } catch (err) {
      ctx.toast?.error?.(err);
      busy = false;
      for (const b of [doSelected, doAll]) b.disabled = false;
    }
  };
  const doSelected = button(t(points.length ? 'td.decide.selected' : 'td.decide.send'), { variant: 'primary', icon: 'check', class: 'td-decide-selected', onClick: () => send(false) });
  const doAll = button(t('td.decide.all'), { variant: 'secondary', class: 'td-decide-all', onClick: () => send(true) });
  return section(t('td.section.decide'), [
    h('p', { class: 'td-note' }, t(points.length ? 'td.decide.intro' : 'td.decide.introFree', { agent })),
    points.length ? h('ul', { class: 'td-decide-list' }, points.map((p, i) => h('li', { class: 'td-decide-item' },
      h('label', { class: 'td-decide-label' }, boxes[i], h('span', {}, p.text))))) : null,
    area,
    error,
    h('div', { class: 'td-decide-actions' }, doAll, doSelected),
  ], { class: 'td-decide' });
}

/**
 * What was decided on a task (a drawer section), or null without a decision.
 * @param {object} task the task detail (decision: {at, byName, all, accepted, refused, comment, taskId, state})
 * @param {number} now
 */
export function decisionSummary(task, now = Date.now()) {
  const d = task?.decision;
  if (!d || typeof d !== 'object') return null;
  const accepted = Array.isArray(d.accepted) ? d.accepted : [];
  const refused = Array.isArray(d.refused) ? d.refused : [];
  return section(t('td.section.decided'), [
    h('p', { class: 'td-note' }, t('td.decided.by', { name: d.byName || '?', when: d.at ? formatDateTime(d.at, now) : '' })),
    d.all ? h('p', {}, t('td.decided.all')) : null,
    accepted.length ? h('ul', { class: 'td-decided-list is-ok' }, accepted.map((x) => h('li', {}, `✓ ${x}`))) : null,
    refused.length ? h('ul', { class: 'td-decided-list is-no' }, refused.map((x) => h('li', {}, `✗ ${x}`))) : null,
    d.comment ? h('blockquote', { class: 'td-decision-feedback' }, d.comment) : null,
    typeof d.taskId === 'string' ? h('p', { class: 'td-decided-follow' },
      h('a', { href: `#/task/${encodeURIComponent(d.taskId)}` }, t('td.decided.followUp')),
      d.state ? stateBadge({ state: d.state, todo: d.todo ?? null }) : null) : null,
  ], { class: 'td-decided' });
}
